import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { MatDrawerToggleResult, MatSidenav } from '@angular/material/sidenav';
import { Router, UrlTree } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  BASE_PATH,
  Configuration,
  ProjectClinicalCaseSecondOpinionControllerApi,
  SecondOpinionControllerApi,
} from 'src/app/sdk-api';

import { HttpErrorResponse } from '../interfaces/http';
import { TdMessageOptions } from '../interfaces/td-message';
import { SecondOpinionFilter } from '../sdk-api/model/secondOpinionFilter';
import { SecondOpinionWithRelations } from '../sdk-api/model/secondOpinionWithRelations';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class SecondOpinionService extends SecondOpinionControllerApi {
  public selectedSecondOpinion$ =
    new BehaviorSubject<SecondOpinionWithRelations | null>(null);
  public newSecondOpinion$ = new Subject<SecondOpinionWithRelations>();
  private secondOpinionSidenav: MatSidenav;

  constructor(
    public projectClinicalCaseSecondOpinionControllerApi: ProjectClinicalCaseSecondOpinionControllerApi,
    private router: Router,
    private translateService: TranslateService,
    protected httpClient: HttpClient,
    @Optional() @Inject(BASE_PATH) basePath: string,
    @Optional() configuration: Configuration,
  ) {
    super(httpClient, basePath, configuration);
  }

  findSecondOpinions(
    projectId: string,
    clinicalCaseId: string,
    filter?: SecondOpinionFilter,
  ): Observable<SecondOpinionWithRelations[]> {
    return this.projectClinicalCaseSecondOpinionControllerApi.projectClinicalCaseSecondOpinionControllerFind(
      projectId,
      clinicalCaseId,
      JSON.stringify(filter) as unknown,
    );
  }

  fullNameReceiver(secondOpinion: SecondOpinionWithRelations): string {
    const { receiver, originalRequest } = secondOpinion;
    if (originalRequest) {
      return `${originalRequest.firstName} ${originalRequest.lastName}`;
    }

    return `${receiver.firstName} ${receiver.lastName}`;
  }

  set sidenav(sidenav: MatSidenav) {
    this.secondOpinionSidenav = sidenav;
  }

  get sidenav(): MatSidenav {
    return this.secondOpinionSidenav;
  }

  closeSidenav(): Promise<MatDrawerToggleResult> {
    return this.secondOpinionSidenav.close();
  }

  openSidenav(): Promise<MatDrawerToggleResult> {
    return this.secondOpinionSidenav.open();
  }

  toggleSidenav(): Promise<MatDrawerToggleResult> {
    return this.secondOpinionSidenav.toggle();
  }

  buildRedirectToSpecificPage(
    secondOpinion: SecondOpinionWithRelations,
  ): UrlTree {
    const { id: secondOpinionId } = secondOpinion;
    const { clinicalCase } = secondOpinion;
    const { id: clinicalCaseId, projectId, patientId } = clinicalCase;
    let url: string[];

    url = patientId
      ? ['/patients', patientId, 'clinical-cases', clinicalCaseId, 'view']
      : ['/projects', projectId, 'clinical-cases', clinicalCaseId, 'view'];

    return this.router.createUrlTree(url, { queryParams: { secondOpinionId } });
  }

  getErrorCreateSecondOpinion(error: HttpErrorResponse): TdMessageOptions {
    if (error.statusCode === 400) {
      return {
        label: this.translateService.instant(UtilsService.labelBadRequest),
        color: 'orange',
        icon: 'warning',
        closeButton: true,
      };
    } else if (error.statusCode === 422 && error.code === 'ENTITY_UNIQUENESS') {
      return {
        label: this.translateService.instant(
          UtilsService.labelEntityUniqueness,
        ),
        color: 'orange',
        icon: 'warning',
        closeButton: true,
      };
    } else {
      return {
        label: this.translateService.instant(UtilsService.labelGenericError),
        color: 'orange',
        icon: 'warning',
        closeButton: true,
      };
    }
  }
}
